import moment from 'moment';
import {IntlShape} from 'react-intl';
import {convertMinutesToFullHour} from '../../../forecast-app/shared/util/convertMinutesToFullHour';
import {humanizeToken} from '../../ai-report-builder/tokenLabel';

/** Shared by the timesheet history drawer and the Time Approval Audit Log report, so one event
 * cannot read two ways depending on where it is opened. */
export const EDIT_FIELD_LABEL_IDS: Record<string, string> = {
	LOGGED_AGAINST: 'time_approval.audit_log.field.logged_against',
	MINUTES_REGISTERED: 'time_approval.audit_log.field.hours',
	DATE: 'time_approval.audit_log.field.date',
	BILLABLE_MINUTES_REGISTERED: 'time_approval.audit_log.field.billable',
	PERSON: 'time_approval.audit_log.field.person',
	ROLE: 'time_approval.audit_log.field.role',
	NOTES: 'time_approval.audit_log.field.notes',
	RETAINER_CONFLICT_HANDLED: 'time_approval.audit_log.field.retainer_conflict',
};

export const editFieldLabel = (fieldName: string, intl: IntlShape): string => {
	const id = EDIT_FIELD_LABEL_IDS[fieldName];
	return id ? intl.formatMessage({id}) : humanizeToken(fieldName);
};

/** Strip structured system prefixes; return null if nothing user-visible remains. */
export const getDisplayReason = (reason: string | null, isBypassEdit: boolean | null): string | null => {
	if (!reason || isBypassEdit) return null;
	// INVOICE_LINK / INVOICE_UNLINK reasons are internal breadcrumbs, not user copy:
	// "INVOICE_LINK: invoice 6462 line 17647". The number in them is the DB invoice id,
	// not the customer-facing companyInvoiceId, which is why these rendered as
	// meaningless high numbers. The invoice field replaces them entirely, and
	// invoiceDeleted covers the case where the invoice is gone — so suppressing here
	// never leaves an invoice event with no reference at all.
	if (reason.startsWith('INVOICE_LINK: ') || reason.startsWith('INVOICE_UNLINK: ')) return null;
	if (reason.startsWith('DATA_IMPORT: ')) {
		const stripped = reason.slice('DATA_IMPORT: '.length).trim();
		return stripped || null;
	}
	if (reason.startsWith('RESTORE: ')) {
		const stripped = reason.slice('RESTORE: '.length).trim();
		return stripped ? ` Restore - ${stripped}` : null;
	}
	if (reason.startsWith('FORCE_APPROVE: ')) {
		const stripped = reason.slice('FORCE_APPROVE: '.length).trim();
		return stripped ? ` Force Approved - ${stripped}` : null;
	}
	return reason;
};

export interface EditSide {
	fieldName: string | null;
	value: string | null;
	label: string | null;
}

/** One side of a field edit in the units a reader thinks in, never the stored string. */
export const formatEditSide = ({fieldName, value, label}: EditSide, intl: IntlShape): string => {
	if (label) return label;
	if (value == null || value === '') return intl.formatMessage({id: 'time_approval.audit_log.value_empty'});
	switch (fieldName) {
		case 'MINUTES_REGISTERED':
		case 'BILLABLE_MINUTES_REGISTERED': {
			const minutes = Number(value);
			return Number.isFinite(minutes) ? String(convertMinutesToFullHour(minutes, intl, true)) : value;
		}
		case 'DATE': {
			const date = moment(value, 'YYYY-MM-DD');
			return date.isValid() ? date.format('D MMM YYYY') : value;
		}
		default:
			return value;
	}
};
